import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Truck, ShieldCheck, PackageCheck, ChevronRight, Minus, Plus } from "lucide-react";
import api, { formatMoney } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { PRODUCT } from "@/constants/testIds";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const { addItem, setDrawerOpen } = useCart();

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => setProduct(r.data));
  }, [slug]);

  if (!product) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-24">
        <div className="animate-pulse grid md:grid-cols-2 gap-10">
          <div className="aspect-square bg-slate-100 rounded-sm" />
          <div className="space-y-4">
            <div className="h-8 w-2/3 bg-slate-100 rounded" />
            <div className="h-4 w-1/2 bg-slate-100 rounded" />
            <div className="h-24 bg-slate-100 rounded mt-6" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-12">
      <nav className="flex items-center gap-2 text-xs text-slate-500 mb-8">
        <Link to="/" className="hover:text-slate-900">Home</Link>
        <ChevronRight className="w-3 h-3" />
        <Link to="/shop" className="hover:text-slate-900">Shop</Link>
        <ChevronRight className="w-3 h-3" />
        <Link to={`/category/${product.category}`} className="hover:text-slate-900 capitalize">
          {product.category.replace("-", " ")}
        </Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-slate-900">{product.name}</span>
      </nav>

      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-7">
          <div className="sticky top-24 space-y-4">
            <div className="aspect-square bg-slate-50 border border-slate-200 rounded-sm overflow-hidden">
              <img
                src={product.image_url}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        <div className="lg:col-span-5">
          <p className="overline text-emerald-800 mb-3">{product.category.replace("-", " ")}</p>
          <h1
            data-testid={PRODUCT.title}
            className="font-display text-3xl md:text-4xl font-black text-slate-900 tracking-tight leading-tight"
          >
            {product.name}
          </h1>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">
            {product.short_description}
          </p>

          <div className="mt-8 border-t border-b border-slate-200 py-6">
            <p
              data-testid={PRODUCT.price}
              className="font-display text-4xl font-black text-slate-900"
            >
              {formatMoney(product.price)}
            </p>
            <p className="text-xs text-slate-500 mt-2">SKU {product.sku} · In stock ({product.stock})</p>
          </div>

          <div className="mt-8">
            <p className="overline text-slate-500 mb-3">Quantity</p>
            <div className="flex items-center gap-4">
              <div className="inline-flex items-center border border-slate-300 rounded-sm">
                <button
                  onClick={() => setQty(Math.max(1, qty - 1))}
                  className="w-10 h-11 flex items-center justify-center hover:bg-slate-100"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <input
                  data-testid={PRODUCT.qty}
                  type="number"
                  value={qty}
                  onChange={(e) => setQty(Math.max(1, parseInt(e.target.value || 1)))}
                  className="w-16 h-11 text-center border-x border-slate-300 focus:outline-none"
                />
                <button
                  onClick={() => setQty(qty + 1)}
                  className="w-10 h-11 flex items-center justify-center hover:bg-slate-100"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <button
                data-testid={PRODUCT.addToCart}
                onClick={() => addItem(product, qty)}
                className="flex-1 py-3 bg-emerald-900 text-white font-semibold text-sm rounded-sm hover:bg-emerald-800 transition-colors"
              >
                Add to cart · {formatMoney(product.price * qty)}
              </button>
            </div>
            <button
              data-testid={PRODUCT.buyNow}
              onClick={() => {
                addItem(product, qty);
                setDrawerOpen(false);
                window.location.href = "/checkout";
              }}
              className="mt-3 w-full py-3 border border-slate-900 text-slate-900 font-semibold text-sm rounded-sm hover:bg-slate-900 hover:text-white transition-colors"
            >
              Buy it now
            </button>
          </div>

          <div className="mt-10 space-y-4">
            <TrustRow icon={Truck} title="No-touch freight" body="Ships directly from certified distributor within 24 hours." />
            <TrustRow icon={ShieldCheck} title="Certified & compliant" body="All items meet FDA, EPA, or ASTM specifications where applicable." />
            <TrustRow icon={PackageCheck} title="Guaranteed accuracy" body="99.98% order accuracy with real-time tracking updates." />
          </div>

          <div className="mt-12">
            <p className="overline text-slate-500 mb-3">Description</p>
            <p className="text-slate-700 leading-relaxed whitespace-pre-line">{product.description}</p>
          </div>

          {product.tags?.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-2">
              {product.tags.map((t) => (
                <span key={t} className="text-xs px-2.5 py-1 bg-slate-100 text-slate-700 rounded-sm">
                  #{t}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TrustRow({ icon: Icon, title, body }) {
  return (
    <div className="flex gap-3 items-start">
      <Icon className="w-5 h-5 text-emerald-900 mt-0.5 flex-shrink-0" />
      <div>
        <p className="font-medium text-slate-900 text-sm">{title}</p>
        <p className="text-xs text-slate-500">{body}</p>
      </div>
    </div>
  );
}
